import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import api from "../api/axios";

const reservationSchema = yup.object().shape({
  name: yup.string().required("Name is required").min(2, "Name must be at least 2 characters"),
  phone: yup.string().required("Phone is required").matches(/^[0-9+\-\s()]+$/, "Please enter a valid phone number"),
  email: yup.string().required("Email is required").email("Please enter a valid email"),
});

export default function ReservationModal({ open, onClose, car }) {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(reservationSchema),
  });

  const handleClose = () => {
    setIsConfirmed(false);
    reset();
    onClose();
  };

  const vehicleRegId = car?.base?.registration || car?.registration || "";
  const make = car?.extended?.make || car?.extended?.extMake || car?.base?.make || car?.make || "";
  const model = car?.extended?.extModel || car?.extended?.model || car?.base?.model || car?.model || "";
  const mileage = car?.base?.mileage || car?.mileage || 0;

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Save reservation request to database
      await api.post("/reservations", {
        vehicle: {
          id: car?.base?.id || car?.id,
          regNumber: vehicleRegId,
          make: make,
          model: model,
          mileage: mileage,
          priceInfo: {
            basePrice: car?.base?.basePrice || car?.basePrice || 0,
            totalPrice: car?.base?.price || car?.price || 0,
            includeWarranty: false,
          },
        },
        fullName: data.name,
        email: data.email,
        phone: data.phone,
        message: "Vehicle reservation request for £99",
      });
      
      setIsConfirmed(true);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (err) {
      console.error("Reservation error:", err);
      toast.error(err.response?.data?.error || "Failed to create reservation");
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[#1f2954]/90 p-4 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto bg-white text-primary rounded-xl shadow-2xl">
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-secondary hover:text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <span className="sr-only">Close</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="p-8">
          {!isConfirmed ? (
            <>
              <h2 className="text-2xl font-bold mb-6 text-center">Reserve for £99</h2>
              
              <p className="text-sm text-secondary mb-6 text-center">
                Please provide your details to reserve this vehicle
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Vehicle Info Display */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-secondary uppercase tracking-wide">Make</p>
                      <p className="text-lg font-semibold text-primary">{make}</p>
                    </div>
                    <div>
                      <p className="text-xs text-secondary uppercase tracking-wide">Mileage</p>
                      <p className="text-lg font-semibold text-primary">{mileage.toLocaleString()} miles</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-secondary uppercase tracking-wide">Model</p>
                    <p className="text-lg font-semibold text-primary">{model}</p>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium mb-1">Name *</label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    {...register("name")}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium mb-1">Phone *</label>
                  <input
                    type="tel"
                    placeholder="Enter your phone number"
                    {...register("phone")}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium mb-1">Email *</label>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    {...register("email")}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-accent text-white py-3 rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition mt-6"
                >
                  {isLoading ? "Processing..." : "Submit Reservation"}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="mb-4 flex justify-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3">Reservation Confirmed!</h3>
              <p className="text-secondary text-center">
                A ZORQK advisor will contact you shortly to complete your reservation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
